import { useEffect, useState } from 'react';
import type { Heading } from '../types';

/**
 * Tracks which heading the reader is currently on.
 *
 * Uses IntersectionObserver where available and falls back to a scroll-position
 * scan, so it still behaves in tests and older browsers.
 */
export const useActiveHeading = (headings: Heading[], offset = 96): string | null => {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (headings.length === 0) {
      setActiveId(null);
      return;
    }

    const ids = headings.map((heading) => heading.id);

    const pickFromScroll = () => {
      let current: string | null = ids[0] ?? null;
      for (const id of ids) {
        const element = document.getElementById(id);
        if (!element) continue;
        if (element.getBoundingClientRect().top - offset <= 0) current = id;
        else break;
      }
      setActiveId(current);
    };

    if (typeof IntersectionObserver === 'undefined') {
      pickFromScroll();
      window.addEventListener('scroll', pickFromScroll, { passive: true });
      return () => window.removeEventListener('scroll', pickFromScroll);
    }

    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = entry.target.id;
          if (entry.isIntersecting) visible.add(id);
          else visible.delete(id);
        }

        const first = ids.find((id) => visible.has(id));
        if (first) setActiveId(first);
        else pickFromScroll();
      },
      { rootMargin: `-${offset}px 0px -70% 0px`, threshold: 0 },
    );

    for (const id of ids) {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    }

    pickFromScroll();
    return () => observer.disconnect();
  }, [headings, offset]);

  return activeId;
};

export default useActiveHeading;
